const careersBlock = (settings = {}) => {
  return {
    settings,
    loading: false,
    error: '',
    careers: [],

    init() {
      this.fetchCareers();
    },

    get groupedCareers() {
      const groups = new Map();

      this.careers.forEach((career) => {
        const country = (career.country || 'Other').trim();

        if (!groups.has(country)) {
          groups.set(country, []);
        }

        groups.get(country).push(career);
      });

      return Array.from(groups.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([country, items]) => ({ country, items }));
    },

    async fetchCareers() {
      this.loading = true;
      this.error = '';

      try {
        const reference = this.$el.dataset.metaobjectReference || this.settings.metaobject_reference || '';
        const typeCandidates = this.getTypeCandidates(reference);

        if (typeCandidates.length === 0) {
          throw new Error('Metaobject reference is required.');
        }

        const entries = await this.fetchMetaobjectEntries(typeCandidates);
        this.careers = entries.map((entry) => this.mapCareer(entry)).filter((career) => career.title);
      } catch (error) {
        console.error('[Careers] Failed to fetch careers:', error);
        this.error = 'Unable to load careers at the moment.';
      } finally {
        this.loading = false;
      }
    },

    getTypeCandidates(reference) {
      const normalized = (reference || '').trim();
      if (!normalized) return [];

      const candidates = [normalized];

      if (normalized.includes('.')) {
        const fallback = normalized.split('.').pop().trim();
        if (fallback && !candidates.includes(fallback)) {
          candidates.push(fallback);
        }
      }

      return candidates;
    },

    mapCareer(entry) {
      const title = this.getFieldValue(entry.fields, 'title') || '';
      const location = this.getFieldValue(entry.fields, 'location') || '';
      const country = this.getFieldValue(entry.fields, 'country') || 'Other';
      const link = this.getFieldValue(entry.fields, 'link') || '';
      const rawTags = this.getFieldValue(entry.fields, 'tags');

      return {
        id: entry.id,
        title,
        location,
        country,
        link,
        tags: this.parseTags(rawTags)
      };
    },

    getFieldValue(fields, key) {
      const field = fields?.find((item) => item.key === key);
      return field?.value || '';
    },

    parseTags(rawTags) {
      if (!rawTags) return [];

      const normalized = rawTags.trim();
      if (!normalized) return [];

      if (normalized.startsWith('[')) {
        try {
          const parsed = JSON.parse(normalized);
          if (Array.isArray(parsed)) {
            return parsed
              .map((item) => (typeof item === 'string' ? item : item?.value || ''))
              .map((item) => item.trim())
              .filter(Boolean);
          }
        } catch {
          // Fall back to comma-separated parsing
        }
      }

      return normalized
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
    },

    async fetchMetaobjectEntries(typeCandidates) {
      const accessToken = window.themeSettings?.storefront_access_token;

      if (!accessToken) {
        throw new Error('Storefront access token not found in window.themeSettings');
      }

      const query = `
        query GetCareerMetaobjects($type: String!) {
          metaobjects(type: $type, first: 250) {
            edges {
              node {
                id
                fields {
                  key
                  value
                }
              }
            }
          }
        }
      `;

      let lastEntries = [];

      for (const type of typeCandidates) {
        const response = await fetch('/api/2023-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': accessToken
          },
          body: JSON.stringify({
            query,
            variables: { type }
          })
        });

        if (!response.ok) {
          throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (data.errors) {
          throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
        }

        const entries = data.data?.metaobjects?.edges?.map((edge) => edge.node) || [];
        lastEntries = entries;

        if (entries.length > 0) {
          return entries;
        }
      }

      return lastEntries;
    }
  };
};

export default {
  name: 'careersBlock',
  component: careersBlock
};
